// SD-1209: the shape of a saved dashboard layout.
//
// Mirrors the JSON stored in dbo.DisplaySettings (SettingsKey = 'dashboard-layout',
// UserID populated -- the invoice-panel rows are the org-level ones with UserID NULL).

/** One card's placement on the 4-column grid. `key` is a DashboardCardDef key. */
export interface DashboardCardLayout {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** A tabbed card's open tab (a key from its def's `tabs`), kept so it reopens where it was left. */
  tab?: string;
}

export type DashboardRowHeight = 'short' | 'standard' | 'tall';

export interface DashboardLayoutSettings {
  /**
   * Bumped when the stored shape changes incompatibly. An unrecognised version falls back to
   * DEFAULT_LAYOUT rather than rendering garbage, so old rows never need a backfill.
   */
  version: number;
  rowHeight: DashboardRowHeight;
  cards: DashboardCardLayout[];
}

export const LAYOUT_VERSION = 1;

/** Pixel height of one grid row, per density. No UI sets this yet; everyone is on standard. */
export const ROW_HEIGHTS: { [k in DashboardRowHeight]: number } = {
  short: 160,
  standard: 190,
  tall: 230,
};

/**
 * What every user sees until they customise: Expenses and Banking, side by side. Banking opens on
 * Transactions; its Expense Reports tab is what was the Statements card.
 *
 * Two 2-wide cards on a 4-column grid is exactly the `grid-template-columns: 1fr 1fr` the
 * dashboard rendered before SD-1209, so shipping this changes nobody's dashboard. It is a
 * starting point, not a restriction -- every other card is in the catalogue and one click away
 * in Add card.
 *
 * Because this is the default, a user who has never customised needs no stored row at all: the
 * endpoint returns null and this is used. There is no backfill.
 */
export const DEFAULT_LAYOUT: DashboardLayoutSettings = {
  version: LAYOUT_VERSION,
  rowHeight: 'standard',
  cards: [
    { key: 'expenses', x: 0, y: 0, w: 2, h: 2 },
    { key: 'banking', x: 2, y: 0, w: 2, h: 2 },
  ],
};

export function cloneLayout(layout: DashboardLayoutSettings): DashboardLayoutSettings {
  return {
    version: layout.version,
    rowHeight: layout.rowHeight,
    cards: layout.cards.map(c => ({ ...c })),
  };
}
